<template>
  <div class="h-100">
    <div class="h-100 d-flex flex-column">
      <div class="d-flex justify-content-end mb-3">
        <b-button v-if="isSaving" variant="primary" size="lg" class="pl-4 pr-4" disabled>
          <b-spinner small />
          {{ 'saving' | i18n }}
        </b-button>
        <b-button v-else-if="isCommonAdmin" variant="primary" size="lg" class="pl-4 pr-4" @click="createTemplate">
          {{ 'save' | i18n }}
        </b-button>
      </div>
      <div class="row poppins d-flex flex-nowrap overflow-auto mr-0" id="main-container" v-if="loaded">
        <div class="col overflow-auto">
          <div class="table-wrapper" ref="mainContentRef">
            <!-- <div class="small-id-text">ID: {{ this.$route.params.id }}</div> -->
            <div class="">
              <div class="row">
                <div class="col-lg-6 col-sm-12 col-xs-12">
                  <Input v-model="caseDetails.name" form-group-id="name" placeholder="name" :required="true" />
                </div>
                <div class="col-lg-6 col-sm-12 col-xs-12">
                  <Input v-model="caseDetails.description" form-group-id="description" placeholder="description" />
                </div>
              </div>
              <div class="row" style="margin-bottom: 10px">
                <div class="col-12 col-lg-12 col-sm-12 col-xs-12 add-button-wrapper">
                  <div>
                    <b-button v-b-modal.templateDetails :variant="isRequiredTemplateDetails ? 'danger' : 'primary'" size="sm mr-2">
                      {{ 'template_details' | i18n }}
                    </b-button>
                    <b-button v-b-modal.automaticTranslation variant="primary" size="sm">
                      {{ 'generate_automatic_translation_for_fields' | i18n }}
                    </b-button>
                  </div>
                  <b-button v-b-modal.groupName variant="primary" size="sm">
                    <BIconPlus /> <span> {{ `Add ${groupName}` | i18n }}</span>
                  </b-button>
                  <TemplateDetails
                    :on-submit="changeTemplateDetails"
                    :showTeams="showTeams"
                    :isTicket="isTicket"
                    :isOne="isOne"
                    :details="caseDetails"
                  />
                  <AutomaticTranslation
                    :selected-translation-languages="selectedTranslationLanguages"
                    :set-selected-translation-languages="setSelectedTranslationLanguages"
                    :on-submit="generateTranslationForAllFields"
                  />
                  <GroupName id="groupName" :on-submit="addNewGroup" :title="`Add ${groupName}`" :isOne="isOne" />
                  <GroupName id="editGroupName" :on-submit="editGroupName" :title="`Edit ${groupName}`" :initData="selectedGroup" :isOne="isOne" />
                </div>
              </div>
              <div ref="listRef" @scroll="scrollListener">
                <div v-show="visibleScroll" class="scroll-to-top" @click="scrollTop">
                  <BIconArrowRightShort />
                </div>
                <draggable
                  :list="groups"
                  :group="{ name: 'groups', put: false }"
                  handle=".handle"
                  :force-fallback="true"
                  :options="{ forceFallback: true }"
                >
                  <div v-for="group in groups" :key="group.id">
                    <Group
                      :group="group"
                      :remove-field="removeField"
                      :duplicate-group="duplicateGroup"
                      :remove-group="removeGroup"
                      :edit-group-name="openEditGroupName"
                      :go-to-field-details="goToFieldDetails"
                      :isOne="isOne"
                      :isMobile="isMobile"
                      :groupName="groupName"
                    />
                  </div>
                </draggable>
                <div v-if="!groups.length" class="no-group">{{ `Please add ${groupName}` | i18n }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-auto d-flex overflow-auto pr-2" v-if="!isMobile">
          <draggable
            :list="types"
            :group="{ name: 'fields', pull: 'clone', put: false }"
            :clone="moveField"
            class="draggable-wrapper"
            :force-fallback="true"
            :options="{ forceFallback: true }"
            @start="isDraging = true"
            @end="isDraging = false"
          >
            <div v-for="type in types" :key="type" class="field-list-item" :draggable="false">
              <div v-if="!isDraging" v-b-tooltip.hover class="icon-overlay" :title="isDraging ? '' : `ENTRY_${type.toUpperCase()}` | i18n" />
              <img class="field-list-icon" :src="icons[type]" v-if="icons[type]">
              <span v-else>{{ `ENTRY_${type.toUpperCase()}` | i18n }}</span>
            </div>
          </draggable>
        </div>
      </div>
      <Loader v-else />
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { BIconPlus, BIconArrowRightShort } from 'bootstrap-vue';
import { mapActions, mapState } from 'vuex';

import { ReportsMixin, DeleteRowMixin, NotificationMixin, AuthMixin } from '../../../../../mixins';

import Input from '../../../common/input';
import Loader from '../../../common/loader';
import Group from './components/group';
import GroupName from './modals/group-name';
import AutomaticTranslation from './modals/automatic-translation';
import TemplateDetails from './modals/template-details';

import icons from './icons';

import {
  ENTRY_TYPES,
  TICKET_TEMPLATE_DETAILS_REQUIRED_FIELDS,
  REPORT_TEMPLATE_DETAILS_REQUIRED_FIELDS,
  ONE_TEMPLATE_DETAILS_REQUIRED_FIELDS,
} from '../../../../../../../../constants';
import { fieldError } from './common';
import api from '../../../../api/index';

export default {
  title: 'Team Details',
  components: {
    Input,
    draggable,
    Group,
    GroupName,
    BIconPlus,
    BIconArrowRightShort,
    Loader,
    AutomaticTranslation,
    TemplateDetails,
  },
  mixins: [ReportsMixin, DeleteRowMixin, NotificationMixin, AuthMixin],
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    trigerPrevent: {
      type: Function,
      default: () => {},
    },
    fieldDetailsRoute: {
      type: String,
      default: '',
    },
    showTeams: {
      type: Boolean,
      default: true,
    },
    isOne: {
      type: Boolean,
      default: false,
    },
    isTicket: {
      type: Boolean,
      default: false,
    },
    groupName: {
      type: String,
      default: 'Group',
    },
    templatesRoute: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isDraging: false,
      visibleScroll: false,
      icons: icons,
      caseDetails: {
        name: '',
        description: '',
        email: '',
        emailCC: '',
        checklistNo: '',
        middlewareId: '',
        teams: [],
        defaultTeam: '',
        defaultUser: '',
      },
      types: Object.values(ENTRY_TYPES),
      groups: [],
      selectedGroup: {},
      loaded: false,
      isMobile: false,
      changed: false,
      isSaving: false,
      selectedTranslationLanguages: [],
    };
  },
  computed: {
    ...mapState({
      templates: (state) => state.template.templates,
    }),
    isRequiredTemplateDetails: function () {
      let currentRequiredFields = REPORT_TEMPLATE_DETAILS_REQUIRED_FIELDS;
      if (this.isOne) currentRequiredFields = ONE_TEMPLATE_DETAILS_REQUIRED_FIELDS;
      if (this.isTicket) currentRequiredFields = TICKET_TEMPLATE_DETAILS_REQUIRED_FIELDS;
      const result = Object.keys(currentRequiredFields).some((key) => {
        if (currentRequiredFields[key]) {
          return Array.isArray(this.caseDetails[key]) ? !this.caseDetails[key].length : !this.caseDetails[key];
        }
        return false;
      });
      return result;
    },
  },
  watch: {
    caseDetails: {
      handler: function (newValue) {
        if (this.loaded) {
          this.changed = true;
        }
      },
      deep: true,
    },
    groups: {
      handler: function (newValue) {
        if (this.loaded) {
          this.changed = true;
        }
      },
      deep: true,
    },
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeFunction);
  },
  async created() {
    window.addEventListener('resize', this.resizeFunction);
    this.resizeFunction();
    this.loaded = false;

    const id = this.$route.params.id;
    const template = this.templates[id];
    if (template && template.caseDetails && template.groups) {
      if (Object.prototype.hasOwnProperty.call(template, 'changed')) {
        this.changed = template.changed;
      }
      const translation = template.caseDetails.translation || {};
      this.groups = template.groups.map((g) => {
        return {
          ...g,
          translation: g.translation || translation[g.id],
          fields: g.fields
            ? g.fields.map((f) => ({
                ...f,
                customKey: Boolean(f.customKey),
                translation: f.translation || translation[f.key],
              }))
            : [],
        };
      });

      let name = template.caseDetails.name;

      if (this.$route.name === 'copy-template' || this.$route.name === 'copy-project-template') {
        if (!name.includes('copy')) {
          name = `${name} copy`;
        }
      }

      this.caseDetails = {
        ...template.caseDetails,
        name,
        teams:
          template.caseDetails.teams && template.caseDetails.teams.length
            ? template.caseDetails.teams.map((item) => (item.id ? { value: item.id, text: item.name } : item))
            : [],
      };
    }

    setTimeout(() => {
      this.loaded = true;

      setTimeout(() => {
        console.log(template && template.offset, 'template && template.offset');
        if (template && template.offset) {
          document.querySelector('.page-content').scrollTo(0, template.offset);
        }
      }, 100);
    }, 100);
  },
  methods: {
    exit() {
      return this.changed;
    },
    resizeFunction() {
      const width = window.innerWidth;
      this.isMobile = width <= 1100;
    },
    ...mapActions({ setTemplate: 'template/setTemplate', setField: 'template/setField', clearTemplates: 'template/clearTemplates' }),
    scrollTop: function () {
      // this.$refs.listRef.scrollTo(0, 0);
    },
    scrollListener: function (e) {
      // this.visibleScroll = e.target.scrollTop > 100;
    },
    getID() {
      return `${Math.round(Math.random() * 10000)}_${new Date().getTime()}`;
    },
    openEditGroupName(group) {
      this.selectedGroup = group;
      setTimeout(() => {
        this.$root.$emit('bv::show::modal', 'editGroupName');
      }, 1);
    },
    duplicateGroup(group) {
      const newGroupId = this.getID();
      const newGroup = {
        ...group,
        id: newGroupId,
        fields: group.fields.map((field) => {
          const newId = this.getID();
          return { ...field, id: newId };
        }),
      };
      const newGroups = [...this.groups];
      const position = newGroups.findIndex(({ id }) => id === group.id);
      newGroups.splice(position + 1, 0, newGroup);

      this.groups = newGroups;
    },
    editGroupName(data) {
      const newGroups = this.groups.map((g) => {
        if (g.id === this.selectedGroup.id) {
          return {
            ...g,
            ...data,
          };
        }
        return g;
      });

      this.groups = newGroups;
      this.selectedGroup = {};
    },
    removeField(groupID, fieldID) {
      this.groups.forEach((g) => {
        if (g.id === groupID) {
          g.fields = g.fields.filter((f) => f.id !== fieldID);
        }
      });
    },
    removeGroup(groupID) {
      this.deleteSelectedRows({}, () => {
        this.groups = this.groups.filter((group) => group.id !== groupID);
      });
    },
    addNewGroup(data) {
      const id = this.getID();

      this.groups.push({
        ...data,
        id,
        fields: [],
      });

      setTimeout(() => {
        this.$refs.mainContentRef.scrollTo(0, this.$refs.mainContentRef.scrollHeight + 500);
      }, 300);
    },
    moveField(e) {
      return {
        type: e,
        name: '',
        require: false,
        editByMobile: true,
        customKey: false,
        id: this.getID(),
      };
    },
    changeTemplateDetails(templateDetailsData) {
      this.caseDetails = {
        ...this.caseDetails,
        ...templateDetailsData,
      };
    },
    async generateTranslationForAllFields(languages) {
      this.loaded = false;
      const allTitles = {};
      this.groups.forEach((g) => {
        g.fields.forEach((f) => {
          allTitles[f.title] = {};
        });
      });

      for (const title in allTitles) {
        for (const i in languages) {
          const ln = languages[i].value;

          try {
            const { data } = await api.translate.translate({ message: title, language: ln });
            allTitles[title][ln] = data;
          } catch (e) {
            this.showNotification({ type: 'error', message: `Problem with generating translation for "${title}"` });
          }
        }
      }

      this.groups.forEach((g) => {
        g.fields.forEach((f) => {
          f.translation = {
            ...f.translation,
            ...allTitles[f.title],
          };
        });
      });

      this.loaded = true;

      console.log(allTitles, 'allTitles');
    },
    goToFieldDetails(field) {
      const offset = document.querySelector('.page-content').scrollTop;

      const id = this.$route.params.id;
      this.setTemplate({ id, template: { groups: this.groups, caseDetails: this.caseDetails, changed: this.changed, offset } });
      this.setField({ field });
      this.$router.push({ name: this.fieldDetailsRoute, params: { id } });
    },
    mapDataBeforeSubmit({ data, isTicket, isOne }) {
      const reportTemplateKeys = ['description', 'name', 'groups', ...Object.keys(REPORT_TEMPLATE_DETAILS_REQUIRED_FIELDS)];
      const ticketTemplateKeys = ['description', 'name', 'groups', ...Object.keys(TICKET_TEMPLATE_DETAILS_REQUIRED_FIELDS)];
      const oneTemplateKeys = ['description', 'name', 'groups', ...Object.keys(ONE_TEMPLATE_DETAILS_REQUIRED_FIELDS)];
      let currentTemplateKeys = reportTemplateKeys;
      if (isTicket) currentTemplateKeys = ticketTemplateKeys;
      if (isOne) currentTemplateKeys = oneTemplateKeys;
      const resultData = {};

      currentTemplateKeys.forEach((key) => {
        resultData[key] = data[key];
      });
      return resultData;
    },
    async createTemplate() {
      try {
        this.isSaving = true;
        if (!this.caseDetails.name) {
          return this.showNotification({
            type: 'error',
            message: this.$translate('Please enter the name for Case Template.'),
          });
        }
        if (this.showTeams && !this.caseDetails.teams.length && !this.isTicket) {
          return this.showNotification({
            type: 'error',
            message: this.$translate('Please select the team.'),
          });
        }
        if (this.isTicket && !this.caseDetails.middlewareId) {
          return this.showNotification({
            type: 'error',
            message: this.$translate('Please select the Middleware ID.'),
          });
        }
        let errorField = {};
        let isField = false;

        this.groups.forEach((g) => {
          g.fields.forEach((f) => {
            isField = true;
            if (fieldError(f)) {
              errorField = f;
            }
          });
        });
        if (!isField) {
          return this.showNotification({
            type: 'error',
            message: this.$translate('Not possible to create an empty Case Template.'),
          });
        }
        if (errorField && errorField.id) {
          return this.showNotification({
            type: 'error',
            message: errorField.title
              ? `${this.$translate('Validation error for field - ')} ${errorField.title}.`
              : this.$translate('Please enter titles for all fields'),
          });
        }

        const data = {
          description: this.caseDetails.description,
          name: this.caseDetails.name,
          groups: this.groups,
          teams: this.showTeams ? this.caseDetails.teams.map((t) => t.value) : [],
          email: this.caseDetails.email,
          emailCC: this.caseDetails.emailCC,
          checklistNo: this.caseDetails.checklistNo,
          middlewareId: this.caseDetails.middlewareId,
          defaultTeam: this.caseDetails.defaultTeam,
          defaultUser: this.caseDetails.defaultUser,
        };

        const paramsData = this.mapDataBeforeSubmit({ data, isTicket: this.isTicket, isOne: this.isOne });

        await this.onSubmit(paramsData);
        this.showNotification({
          type: 'success',
          message: this.$translate('success'),
        });
        this.clearTemplates();
        this.trigerPrevent(true);
        this.$router.push({ name: this.templatesRoute });
      } catch (err) {
        console.log(err);
        this.trigerPrevent(false);
        this.showNotification({ type: 'error', message: err.message });
      } finally {
        this.isSaving = false;
      }
    },
    setSelectedTranslationLanguages(languagesList) {
      this.selectedTranslationLanguages = languagesList;
    },
  },
};
</script>

<style scoped lang="scss">
.inputs-block {
  padding-left: 10px;
  padding-right: 10px;
}
.add-button-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.always-full {
  min-height: calc(100vh - 120px);
}

.field-list-item {
  border-bottom: 1px solid #d9d9d9;
  padding: 3px;
  padding: 7px 10px;
  // margin-bottom: 5px;
  // padding-bottom: 10px;
  // padding-left: 10px;
  // padding-right: 10px;
  width: 100%;
  cursor: move;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently*/
  background: white;
  position: relative;
  text-align: center;
}

.icon-overlay {
  position: absolute;
  width: 50px;
  height: 50px;
}

.field-list-icon {
  width: 50px;
}

.field-wrapper {
  position: absolute;
  bottom: 10px;
  right: 20px;
}

.field-list-name {
  margin-left: 10px;
}

.no-group {
  text-align: center;
  margin: 100px 0;
  color: rgba(0, 0, 8, 0.46);
}

.scroll-to-top {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 28px;
  margin: auto;
  transform: rotate(-90deg);
  position: fixed;
  z-index: 2000;
  right: 95px;
  bottom: 27px;
  cursor: pointer;
}

.fields-button {
  min-width: 136px;
  margin-left: 10px;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
